import { Component, inject, OnDestroy, TemplateRef, ViewChild } from "@angular/core";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { HistoryService } from "../../services/history.service";
import { Helper } from "src/app/core/helpers/helper";
import { ChatService } from "../../services/chat.service";
import { SocketService } from "../../services/socket.service";
import { firstValueFrom, Subscription } from "rxjs";
import { BookingStatus } from "src/app/core/constants/bookingConstant";
import { MapHelperService } from "../../services/mapInit.service";
import { CommonService } from "../../services/common.service";
import { LanguageService } from "src/app/core/services/language.service";
import { VehicleService } from "../../services/vehicle.service";
import { FormFieldService } from "../../services/form-field.service";

declare const google: any;

@Component({
  selector: "app-booking-details-modal",
  templateUrl: "./booking-details.component.html",
  styleUrl: "./booking-details.component.scss",
  standalone: false
})
export class BookingDetailsModalComponent implements OnDestroy {
  private session = 0;
  private destroyed = false;
  activeTab: number = 1;
  decimalPointValue: number = 0;
  setCurrencySign: number = 1;
  locationDistance: number = 0;
  chatActiveTab: number = 1;

  bookingHistoryMainType: string = "";
  tabType: string = "";
  chatTabType: string = "";
  bookingId: string = "";
  bookingTimezone: string = "";

  bookingDetailsGoogleMap: any;
  bookingDetailsMapBoxMap: any;
  /** Loaded with `import("mapbox-gl")` only when a Mapbox map is opened. */
  private mb: any = null;
  bookingDetails: any;
  pickupMarker: any;
  destinationMarker: any;
  driverMarker: any;
  bookingInvoiceDetails: any;
  bookingTimesDetails: any;
  tripPath: any;
  driverToPickupPath: any;
  totalTimeInterval: any;
  waitingTimeInterval: any;
  standByTimeInterval: any;
  estimatedBookingPath: any;
  modalConfig: any = { size: "fullscreen", windowClass: "modal-holder" };

  tripLocations: Array<any> = [];
  driverToPickupLocations: Array<any> = [];
  bookingStatusTimeline: Array<any> = [];
  accessibilityList: Array<any> = [];
  formFieldList: Array<any> = [];
  bookingSpeakingLanguages: Array<any> = [];
  customPrices: Array<any> = [];

  bookingDetailsModalRef!: NgbModalRef;
  mapModalRef!: NgbModalRef;

  driverLocationSubscription!: Subscription;
  bookingStatusSocketSubscription!: Subscription;

  private historyService = inject(HistoryService);
  private commonService = inject(CommonService);
  private chatService = inject(ChatService);
  private socketService = inject(SocketService);
  private mapHelper = inject(MapHelperService);
  public languageService = inject(LanguageService);
  private vehicleService = inject(VehicleService);
  private formFieldService = inject(FormFieldService);

  @ViewChild("bookingDetailsModal", { static: true }) bookingDetailsModal!: TemplateRef<any>;
  @ViewChild("mapModal", { static: true }) mapModal!: TemplateRef<any>;

  constructor(public modalService: NgbModal, public helper: Helper) {}

  /**
   * Opens the booking details modal and initializes necessary data.
   *
   * @param bookingHistoryMainType The main type of booking history.
   * @param bookingId The ID of the booking.
   */
  async openModal(bookingHistoryMainType: string, bookingId: string, initialTab = 1): Promise<void> {
    if (this.destroyed) return;
    this.closeModal();
    const session = this.session;
    this.chatTabType = "";
    // Logs are part of the Activities tab now, so an old "Logs" entry point (tab 7 — the incoming
    // call modal still has one) lands on Activities instead of an empty pane.
    this.activeTab = initialTab === 7 ? 4 : initialTab;
    this.tabType = this.activeTab === 4 ? "activities" : this.activeTab === 8 ? "vehicleTrack" : "basicDetails";
    this.bookingHistoryMainType = bookingHistoryMainType;

    this.bookingId = bookingId;
    try {
      await Promise.all([this.getAdminVehicleAccessibilityList(), this.getFormFieldList()]);
      if (session !== this.session || this.destroyed) return;
      await this.getBookingDetails({ bookingId });
    } catch {
      if (session === this.session) this.clearDefaults();
    }
  }

  /**
   * Fetches booking details based on the booking ID and updates the component state.
   *
   * @param bookingId The ID of the booking.
   * @param fromSocket Indicates if the request is from a socket event.
   */
  async getBookingDetails({ bookingId, fromSocket = false }: { bookingId: string; fromSocket?: boolean }): Promise<void> {
    const session = this.session;
    let apiRequest: any;
    if (this.bookingHistoryMainType === this.helper.BookingHistoryMainType.RUNNING || this.bookingHistoryMainType === this.helper.BookingHistoryMainType.SCHEDULED) {
      apiRequest = this.historyService.getRunningBookingDetails(bookingId);
    } else {
      apiRequest = this.historyService.getBookingDetails(bookingId);
    }

    const response: any = await firstValueFrom(apiRequest); // Use firstValueFrom instead of toPromise

    if (session !== this.session || this.destroyed) return;
    if (response?.booking) {
      this.bookingDetails = response.booking;
      this.bookingStatusTimeline = response.booking?.statusTimeline;
      this.bookingTimezone = this.bookingDetails.timeZone ?? this.helper.displayTimezone;

      if (this.bookingDetails.speakingLanguages?.length > 0) {
        this.bookingSpeakingLanguages = this.languageService.supportedLanguages
          .filter((language) => this.bookingDetails.speakingLanguages.includes(language.code))
          .map((language) => language.label);
      }

      this.setBookingInvoiceDetails();

      this.decimalPointValue = this.bookingDetails.decimalPointValue ?? this.bookingDetails.bookingInvoice?.decimalPointValue;
      this.setCurrencySign = this.bookingDetails.setCurrencySign ?? this.bookingDetails.bookingInvoice?.setCurrencySign;

      this.clearTimers();
      this.setTimer();

      this.setIdsToNames();
      this.mapAddressMetadataToKeys(this.bookingDetails, this.formFieldList);

      if (!fromSocket) {
        this.setBookingHistory();
      }
    }
  }

  /**
   * Sets the booking invoice details based on the booking history type.
   */
  setBookingInvoiceDetails(): void {
    if (this.bookingHistoryMainType === this.helper.BookingHistoryMainType.COMPLETED || this.bookingHistoryMainType === this.helper.BookingHistoryMainType.CANCELLED) {
      this.bookingInvoiceDetails = this.bookingDetails.bookingInvoice?.actual;
      this.bookingTimesDetails = this.bookingDetails.bookingInvoice?.actual;
    } else {
      this.bookingInvoiceDetails = this.bookingDetails.bookingInvoice?.estimated;
      this.bookingTimesDetails = this.bookingDetails.bookingInvoice?.estimated;
    }

    if (this.bookingHistoryMainType === this.helper.BookingHistoryMainType.RUNNING) {
      this.bookingTimesDetails = this.bookingDetails.bookingInvoice?.actual;
    }
  }

  /**
   * Fetches the list of vehicle accessibility options for the admin.
   *
   * @returns A promise that resolves with the accessibility list.
   */
  async getAdminVehicleAccessibilityList(): Promise<any> {
    const session = this.session;
    const response: any = await firstValueFrom(this.vehicleService.getAccessibilityList({}));
    if (session !== this.session || this.destroyed) return;
    this.accessibilityList = response?.accessibilities || [];
    this.customPrices = response?.customPrices || [];
    return this.accessibilityList;
  }

  async getFormFieldList(): Promise<any> {
    const session = this.session;
    const response: any = await firstValueFrom(this.formFieldService.getFormFieldList({ type: "ADDRESS" }));
    if (session !== this.session || this.destroyed) return;
    this.formFieldList = response?.formFields || [];
    return this.formFieldList;
  }

  mapAddressMetadataToKeys(bookingDetails: any, formFieldList: any[]): void {
    if (!bookingDetails?.destinationAddresses || !formFieldList) return;

    const fieldMap = new Map(formFieldList.map((field) => [field._id, field.label ?? field.key]));

    const mapMetadata = (metadata: any[]): any =>
      metadata.map((item) => ({
        key: fieldMap.get(item.id) ?? item.id,
        value: item.value
      }));

    // Map pickup
    if (bookingDetails.pickupAddress?.metadata?.length > 0) {
      bookingDetails.pickupAddress.metadata = mapMetadata(bookingDetails.pickupAddress.metadata);
    }

    // Map destinations
    bookingDetails.destinationAddresses = bookingDetails.destinationAddresses.map((address: any) => ({
      ...address,
      metadata: address.metadata?.length > 0 ? mapMetadata(address.metadata) : []
    }));

    // Map actual destinations (stops)
    bookingDetails.actualDestinationAddresses =
      bookingDetails.actualDestinationAddresses?.map((address: any) => ({
        ...address,
        metadata: address.metadata?.length > 0 ? mapMetadata(address.metadata) : []
      })) ?? [];
  }
  /**
   * Maps accessibility IDs and vehicle type IDs to their respective names.
   */
  setIdsToNames(): void {
    if (this.bookingDetails.accessibilityIds?.length > 0) {
      this.commonService.getAccessibilityList().then((accessibilityList) => {
        this.accessibilityList = accessibilityList;
        this.commonService.mapAccessibilityIdsToNames(this.bookingDetails.accessibilityIds).then((accessibilityNamesArray) => {
          this.bookingDetails.accessibilityNamesArray = accessibilityNamesArray;
        });
      });
    }
    if (!this.bookingDetails.vehicleType?.name && this.bookingDetails.vehicleTypeId) {
      this.commonService.mapVehicleTypeIdsToNames([this.bookingDetails.vehicleTypeId]).then((vehicleTypeNamesArray) => {
        const vehicleTypeNames = vehicleTypeNamesArray.join(", ");
        this.bookingDetails.vehicleTypeNames = vehicleTypeNames;
      });
    }
  }

  /**
   * Sets the booking history and initializes the map and socket connections.
   */
  setBookingHistory(): void {
    this.openBookingDetailsModal();
    const session = this.session;

    this.getBookingLocation().then(() => {
      setTimeout(() => {
        if (session !== this.session || this.destroyed) return;
        if (this.isNotQuickCommerceBooking()) {
          if (this.helper.mapTypeMapBox()) {
            this.setMapBoxMap();
          }
          if (this.helper.mapTypeGoogle()) {
            this.setGoogleMap();
          }
        }

        if (this.bookingHistoryMainType === this.helper.BookingHistoryMainType.RUNNING) {
          this.initDriverSocket();
        }
      }, 500);
    });
  }

  /**
   * Sets timers for total time, waiting time, and standby time.
   */
  setTimer(): void {
    if (!this.bookingTimesDetails || this.bookingHistoryMainType !== this.helper.BookingHistoryMainType.RUNNING) return;

    // Show Total Time and Distance Timer
    this.bookingTimesDetails.time = this.bookingTimesDetails?.time || 0;
    this.totalTimeInterval = setInterval(() => {
      if (this.bookingDetails.status !== this.helper.BookingStatus.STARTED) {
        this.clearTotalTimeTimer();
      } else {
        this.bookingTimesDetails.time++;
      }
    }, 1000);

    // Show Waiting Time Timer
    this.bookingTimesDetails.waitingTime = this.bookingTimesDetails?.waitingTime || 0;
    this.waitingTimeInterval = setInterval(() => {
      if (this.bookingDetails.status >= this.helper.BookingStatus.STARTED) {
        this.clearWaitingTimeTimer();
      } else {
        this.bookingTimesDetails.waitingTime++;
      }
    }, 1000);

    // Stand By Time Timer
    this.bookingTimesDetails.stopWaitingTime = this.bookingTimesDetails?.stopWaitingTime || 0;
    this.standByTimeInterval = setInterval(() => {
      if (this.bookingDetails.status !== this.helper.BookingStatus.ARRIVED_AT_STOP) {
        this.clearStandByTimeTimer();
      } else {
        this.bookingTimesDetails.stopWaitingTime++;
      }
    }, 1000);
  }

  /**
   * Clears all active timers.
   */
  clearTimers(): void {
    this.clearTotalTimeTimer();
    this.clearWaitingTimeTimer();
    this.clearStandByTimeTimer();
  }

  /**
   * Clears the total time timer.
   */
  clearTotalTimeTimer(): void {
    if (this.totalTimeInterval) {
      clearInterval(this.totalTimeInterval);
    }
  }

  /**
   * Clears the waiting time timer.
   */
  clearWaitingTimeTimer(): void {
    if (this.waitingTimeInterval) {
      clearInterval(this.waitingTimeInterval);
    }
  }

  /**
   * Clears the standby time timer.
   */
  clearStandByTimeTimer(): void {
    if (this.standByTimeInterval) {
      clearInterval(this.standByTimeInterval);
    }
  }

  /**
   * Formats a given time in seconds to a string in HH:MM:SS format.
   *
   * @param seconds The time in seconds.
   * @returns The formatted time string.
   */
  formatTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    const paddedHours = String(hours).padStart(2, "0");
    let paddedMinutes;
    let paddedSeconds;
    if (this.bookingHistoryMainType === this.helper.BookingHistoryMainType.RUNNING) {
      paddedMinutes = String(Math.abs(minutes)).padStart(2, "0");
      paddedSeconds = String(Math.abs(secs)).padStart(2, "0");
    } else {
      paddedMinutes = String(minutes).padStart(2, "0");
      paddedSeconds = String(secs).padStart(2, "0");
    }

    if (Number(paddedHours) > 0) {
      return `${paddedHours}:${paddedMinutes}:${paddedSeconds} ${this.helper.translateService.instant("label-title.hour")}`;
    }
    return `${paddedMinutes}:${paddedSeconds} ${this.helper.translateService.instant("label-title.min")}`;
  }

  /**
   * Checks if the booking is not a rental booking.
   *
   * @returns True if the booking is not a rental booking, false otherwise.
   */
  isNotRentalBooking(): boolean {
    return this.bookingDetails.bookingType !== this.helper.PriceMode.RENTAL;
  }

  /**
   * Checks if the booking is a rental booking.
   *
   * @returns True if the booking is a rental booking, false otherwise.
   */
  isRentalBooking(): boolean {
    return this.bookingDetails.bookingType === this.helper.PriceMode.RENTAL;
  }

  /**
   * Checks if the booking is not a quick commerce booking.
   *
   * @returns True if the booking is not a quick commerce booking, false otherwise.
   */
  isNotQuickCommerceBooking(): boolean {
    return this.bookingDetails.bookingType !== this.helper.PriceMode.QUICK_COMMERCE;
  }

  /**
   * Checks if the booking is a quick commerce booking.
   *
   * @returns True if the booking is a quick commerce booking, false otherwise.
   */
  isQuickCommerceBooking(): boolean {
    return this.bookingDetails.bookingType === this.helper.PriceMode.QUICK_COMMERCE;
  }

  /**
   * Fetches the booking location details.
   *
   * @returns A promise that resolves with a boolean indicating success.
   */
  getBookingLocation(): Promise<boolean> {
    const session = this.session;
    return new Promise((resolve) => {
      this.historyService.getBookingLocation(this.bookingId, { isShowErrorToast: false }).subscribe((response: any) => {
        if (session !== this.session || this.destroyed) { resolve(false); return; }
        if (response?.data) {
          this.locationDistance = Number(response.data.distance);
          response.data.locations?.forEach((location: any) => {
            this.tripLocations.push({ lat: location.latitude, lng: location.longitude });
          });
          response.data.pickupLocations?.forEach((location: any) => {
            this.driverToPickupLocations.push({ lat: location.latitude, lng: location.longitude });
          });
          resolve(true);
        } else {
          resolve(true);
        }
      });
    });
  }

  /**
   * Initializes the driver socket connection for live updates.
   */
  initDriverSocket(): void {
    if (this.bookingDetails.confirmedDriver) {
      const parameters: any = {
        data: {
          roomId: this.bookingDetails.confirmedDriver.id
        }
      };
      this.socketService.sendAndListenEvent(this.helper.SocketEvents.JOIN_ROOM, parameters, (data: any) => {
        if (data.data) {
          this.getDriverLocation();
        }
      });
    }

    if (this.bookingHistoryMainType === this.helper.BookingHistoryMainType.RUNNING || this.bookingHistoryMainType === this.helper.BookingHistoryMainType.SCHEDULED) {
      const BookingSocketParameters: any = {
        data: {
          roomId: this.bookingDetails._id
        }
      };
      this.socketService.emit(this.helper.SocketEvents.JOIN_ROOM, BookingSocketParameters);

      this.bookingStatusSocketSubscription = this.socketService.listener(this.helper.SocketEvents.BOOKING_STATUS).subscribe((res: any) => {
        if (res.data?.bookingId === this.bookingId) {
          this.bookingDetails.status = res.data.status;
          if (res.data.status >= this.helper.BookingStatus.STARTED) {
            this.clearWaitingTimeTimer();
          }
          if (res.data.status >= this.helper.BookingStatus.ARRIVED_AT_DESTINATION) {
            this.bookingHistoryMainType = this.helper.BookingHistoryMainType.COMPLETED;
            this.clearTimers();
          }
          this.getBookingDetails({ bookingId: this.bookingId, fromSocket: true });
        }
      });
    }
  }

  /**
   * Fetches the driver's live location.
   */
  getDriverLocation(): void {
    this.driverLocationSubscription = this.socketService.listener(this.helper.SocketEvents.DRIVER_LIVE_LOCATION).subscribe((res: any) => {
      this.locationDistance = res?.data?.distanceList[0]?.distance ?? 0;
      if (this.bookingDetails.status < this.helper.BookingStatus.ARRIVED_AT_DESTINATION) {
        if (res.data?.distanceList?.length > 0) {
          this.bookingInvoiceDetails.distance = res.data.distanceList[0].distance;
          this.bookingInvoiceDetails.trafficTime = res.data.distanceList[0].trafficTime;
        }
        if (res.data && this.helper.mapTypeGoogle()) {
          this.setPathAndDriverLocationForGoogleMap(res);
        } else if (res.data && this.helper.mapTypeMapBox()) {
          this.setPathAndDriverLocationForMapBoxMap(res);
        }
      }
    });
  }

  /**
   * Sets the path and driver location on the Google map.
   *
   * @param res The response data containing location details.
   */
  setPathAndDriverLocationForGoogleMap(res: any): void {
    for (const location of res.data.locations) {
      const latlng = { lat: location.latitude, lng: location.longitude };
      const googleLatLng: any = new google.maps.LatLng(latlng);

      if (this.bookingDetails.status < this.helper.BookingStatus.ARRIVED_AT_PICKUP) {
        this.driverToPickupLocations.push(latlng);
        this.driverToPickupPath?.setPath(this.driverToPickupLocations);
      } else {
        this.tripLocations.push(latlng);
        this.tripPath?.setPath(this.tripLocations);
      }
      this.driverMarker?.setPosition(googleLatLng);
      this.bookingDetailsGoogleMap.panTo(googleLatLng);
    }
  }

  /**
   * Gets the distance for the booking.
   *
   * @returns The distance for the booking.
   */
  getDistance(): number {
    if (this.bookingHistoryMainType === this.helper.BookingHistoryMainType.RUNNING && this.isNotQuickCommerceBooking()) {
      return this.locationDistance;
    }

    if (this.bookingHistoryMainType === this.helper.BookingHistoryMainType.SCHEDULED || this.isQuickCommerceBooking()) {
      return this.bookingDetails?.bookingInvoice?.estimated?.distance ?? 0;
    }

    return this.bookingDetails?.bookingInvoice?.actual?.distance ?? 0;
  }

  /**
   * Sets the path and driver location on the Mapbox map.
   *
   * @param res The response data containing location details.
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  async setPathAndDriverLocationForMapBoxMap(res: any): Promise<void> {
    this.mb = this.mb || (await this.mapHelper.loadMapbox());
    for (const location of res.data.locations) {
      const latlng: [number, number] = [location.longitude, location.latitude];

      // Move driver marker
      if (this.driverMarker) {
        this.driverMarker.setLngLat(latlng);
      } else {
        this.driverMarker = new this.mb.Marker({
          draggable: false,
          element: this.createCustomMarkerElement(this.helper.MapPinImages.DriverMapPin)
        })
          .setLngLat(latlng)
          .addTo(this.bookingDetailsMapBoxMap);
      }

      this.bookingDetailsMapBoxMap.panTo(latlng);

      const isBeforePickup = this.bookingDetails.status < this.helper.BookingStatus.ARRIVED_AT_PICKUP;

      if (isBeforePickup) {
        this.driverToPickupLocations.push({ lng: latlng[0], lat: latlng[1] });

        if (this.driverToPickupLocations.length >= 2) {
          const coords = this.driverToPickupLocations.map((loc) => [loc.lng, loc.lat]);

          const geoJson: GeoJSON.Feature<GeoJSON.LineString> = {
            type: "Feature",
            properties: {},
            geometry: {
              type: "LineString",
              coordinates: coords
            }
          };

          // Ensure source exists before updating
          if (this.bookingDetailsMapBoxMap.getSource("driverToPickupPath")) {
            const source = this.bookingDetailsMapBoxMap.getSource("driverToPickupPath") as any;
            source.setData(geoJson);
          } else {
            // If source doesn't exist, wait for map to be idle and then create it
            this.bookingDetailsMapBoxMap.once("idle", () => {
              if (!this.bookingDetailsMapBoxMap.getSource("driverToPickupPath")) {
                this.bookingDetailsMapBoxMap.addSource("driverToPickupPath", {
                  type: "geojson",
                  data: geoJson
                });

                this.bookingDetailsMapBoxMap.addLayer({
                  id: "driverToPickupPath",
                  type: "line",
                  source: "driverToPickupPath",
                  layout: {
                    "line-join": "round",
                    "line-cap": "round"
                  },
                  paint: {
                    "line-color": "#00FF00",
                    "line-opacity": 1.0,
                    "line-width": 3
                  }
                });

                this.driverToPickupPath = this.bookingDetailsMapBoxMap.getSource("driverToPickupPath") as any;
              }
            });
          }
        }
      } else {
        this.tripLocations.push({ lng: latlng[0], lat: latlng[1] });

        if (this.tripLocations.length >= 2) {
          const coords = this.tripLocations.map((loc) => [loc.lng, loc.lat]);

          const geoJson: GeoJSON.Feature<GeoJSON.LineString> = {
            type: "Feature",
            properties: {},
            geometry: {
              type: "LineString",
              coordinates: coords
            }
          };

          if (this.bookingDetailsMapBoxMap.getSource("tripPath")) {
            const source = this.bookingDetailsMapBoxMap.getSource("tripPath") as any;
            source.setData(geoJson);
          } else {
            this.bookingDetailsMapBoxMap.once("idle", () => {
              if (!this.bookingDetailsMapBoxMap.getSource("tripPath")) {
                this.bookingDetailsMapBoxMap.addSource("tripPath", {
                  type: "geojson",
                  data: geoJson
                });

                this.bookingDetailsMapBoxMap.addLayer({
                  id: "tripPath",
                  type: "line",
                  source: "tripPath",
                  layout: {
                    "line-join": "round",
                    "line-cap": "round"
                  },
                  paint: {
                    "line-color": "#FF0000",
                    "line-opacity": 1.0,
                    "line-width": 3
                  }
                });

                this.tripPath = this.bookingDetailsMapBoxMap.getSource("tripPath") as any;
              }
            });
          }
        }
      }
    }
  }

  // setEstimatedPathOnMapboxMap(): void {
  //   if (
  //     this.bookingDetails?.bookingInvoice?.estimated?.directionPath &&
  //     this.bookingHistoryMainType !== this.helper.BookingHistoryMainType.COMPLETED &&
  //     !this.estimatedBookingPath
  //   ) {
  //     // Decode polyline using @mapbox/polyline
  //     const decodedCoordinates = google.maps.geometry.encoding.decodePath(this.bookingDetails.bookingInvoice.estimated.directionPath);

  //     // Convert to [lng, lat] format
  //     const coordinates: [number, number][] = decodedCoordinates.map((coord: number[]) => [coord[1], coord[0]]);

  //     const geoJson: GeoJSON.Feature<GeoJSON.LineString> = {
  //       type: 'Feature',
  //       properties: {},
  //       geometry: {
  //         type: 'LineString',
  //         coordinates
  //       }
  //     };

  //     // Add source and layer to mapbox if not already present
  //     if (!this.bookingDetailsMapBoxMap.getSource('estimatedBookingPath')) {
  //       this.bookingDetailsMapBoxMap.addSource('estimatedBookingPath', {
  //         type: 'geojson',
  //         data: geoJson
  //       });

  //       this.bookingDetailsMapBoxMap.addLayer({
  //         id: 'estimatedBookingPath',
  //         type: 'line',
  //         source: 'estimatedBookingPath',
  //         layout: {
  //           'line-join': 'round',
  //           'line-cap': 'round'
  //         },
  //         paint: {
  //           'line-color': '#000000',
  //           'line-opacity': 1.0,
  //           'line-width': 3
  //         }
  //       });

  //       this.estimatedBookingPath = this.bookingDetailsMapBoxMap.getSource('estimatedBookingPath') as any;
  //     }
  //   }
  // }

  /**
   * Opens the booking details modal.
   */
  openBookingDetailsModal(): void {
    const session = this.session;
    const ref = this.modalService.open(this.bookingDetailsModal, this.modalConfig);
    this.bookingDetailsModalRef = ref;
    const cleanup = () => {
      if (session !== this.session) return;
      this.session++;
      this.clearDefaults();
    };
    ref.result.then(cleanup, cleanup);
  }

  closeModal(): void {
    this.session++;
    this.mapModalRef?.dismiss("Booking closed");
    this.bookingDetailsModalRef?.dismiss("Cross click");
    this.clearDefaults();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.closeModal();
  }

  /**
   * Clears default values and disconnects sockets.
   */
  clearDefaults(): void {
    this.chatService.disConnectChatSockets();
    this.chatTabType = "";

    this.tripLocations = [];
    this.driverToPickupLocations = [];
    this.bookingSpeakingLanguages = [];

    if (this.helper.mapTypeGoogle()) {
      if (this.driverToPickupPath) {
        this.driverToPickupPath.setMap(null);
        this.driverToPickupPath = null;
      }
      if (this.tripPath) {
        this.tripPath.setMap(null);
        this.tripPath = null;
      }
      if (this.estimatedBookingPath) {
        this.estimatedBookingPath.setMap(null);
        this.estimatedBookingPath = null;
      }
    }

    if (this.helper.mapTypeMapBox()) {
      this.removeDriverToPickupTripPath();
      this.removeTripPath();
      this.bookingDetailsMapBoxMap?.remove();
      this.bookingDetailsMapBoxMap = null;
    }
    this.bookingDetailsGoogleMap = null;
    this.tripPath = null;
    this.driverToPickupPath = null;
    this.estimatedBookingPath = null;
    this.pickupMarker = null;
    this.destinationMarker = null;
    this.driverMarker = null;

    this.clearTimers();

    this.driverLocationSubscription?.unsubscribe();
    this.bookingStatusSocketSubscription?.unsubscribe();
  }

  /**
   * Gets the status string for a given status code.
   *
   * @param status The status code.
   * @returns The status string.
   */
  getStatusString(status: number): string {
    return this.helper.BookingStatusStrings[status as BookingStatus];
  }

  /**
   * Theme tone for the trip status pill. Same grouping on every trip, running or archived.
   */
  statusTone(status: number): string {
    const s = this.helper.BookingStatus;
    if ([s.NO_DRIVER_FOUND, s.NOT_ANSWERED, s.REJECTED, s.CANCELLED, s.MERCHANT_REJECTED, s.BID_REJECTED].includes(status)) return "danger";
    if ([s.ACCEPTED, s.ARRIVED_AT_DESTINATION, s.COMPLETED, s.CUSTOMER_INVOICE_SUBMITTED, s.DRIVER_INVOICE_SUBMITTED].includes(status)) return "ok";
    if ([s.ARRIVED_AT_PICKUP, s.STARTED, s.ARRIVED_AT_STOP, s.PICKED, s.SERVICE_STARTED, s.ARRIVED_NEAR_DESTINATION].includes(status)) return "move";
    return "wait";
  }

  /**
   * Gets the booking tags as strings.
   *
   * @param bookingTags The array of booking tags.
   * @returns The array of booking tags as strings.
   */
  getBookingTagsString(bookingTags: Array<string>): Array<any> {
    if (!bookingTags?.length) return [];
    return bookingTags
      .map((tag) => {
        const key = (this.helper.BookingType as any)[tag];
        let string: string = "";

        if (key && this.helper.getBookingTagColor(tag)) {
          string = this.helper.translateService.instant(this.helper.BookingTypeString[key]);
          return { tag, string };
        }

        return null;
      })
      .filter((item) => item !== null); // Filter out null values
  }

  /**
   * Handles the error event for the customer image.
   *
   * @param event The error event.
   */
  handleCustomerImageError(event: any): void {
    event.target.src = this.helper.DefaultImages.DefaultUserImage;
    if (this.bookingDetails.customerDetail) {
      this.bookingDetails.customerDetail.imageUrl = "";
    }
  }

  /**
   * Handles the error event for the driver image.
   *
   * @param event The error event.
   */
  handleDriverImageError(event: any): void {
    event.target.src = this.helper.DefaultImages.DefaultUserImage;
    if (this.bookingDetails.confirmedDriver) {
      this.bookingDetails.confirmedDriver.imageUrl = "";
    }
  }

  /**
   * Handles the error event for the merchant image.
   *
   * @param event The error event.
   */
  handleMerchantImageError(event: any): void {
    event.target.src = this.helper.DefaultImages.DefaultUserImage;
    if (this.bookingDetails.merchantDetail) {
      this.bookingDetails.merchantDetail.imageUrl = "";
    }
  }

  /**
   * Sets the legend on the map.
   */
  setLegendOnMap(): void {
    const icons: Record<string, any> = {
      pickupLocation: {
        name: this.helper.translateService.instant("label-title.pickup"),
        icon: this.helper.MapPinImages.PickupMapPin
      },
      destinationLocation: {
        name: this.helper.translateService.instant("label-title.destination"),
        icon: this.helper.MapPinImages.DestinationMapPin
      },
      stopLocation: {
        name: this.helper.translateService.instant("label-title.stops"),
        icon: this.helper.MapPinImages.StopMapPin
      },
      driverLocation: {
        name: this.helper.translateService.instant("label-title.driver"),
        icon: this.helper.MapPinImages.DriverMapPin
      }
    };

    const legend = document.getElementById("legend");

    for (const key in icons) {
      const type = icons[key];
      const name = type.name;
      const icon = type.icon;
      const div = document.createElement("div");

      div.innerHTML = `<img src="${icon}"> ${name}`;
      div.classList.add("legend-item"); // Add a class for styling
      legend?.appendChild(div);
    }

    if (this.helper.mapTypeGoogle()) {
      const controls = this.bookingDetailsGoogleMap?.controls;
      if (controls && legend) controls[google.maps.ControlPosition.RIGHT_BOTTOM]?.push(legend);
    } else if (this.helper.mapTypeMapBox() && this.bookingDetailsMapBoxMap) {
      // For Mapbox
      class LegendControl {
        onAdd(): HTMLElement {
          const container = document.createElement("div");
          container.id = "legend";
          container.innerHTML = legend?.innerHTML ?? "";
          container.className = "mapboxgl-ctrl";
          return container;
        }
        onRemove(): void {
          const _legend = document.getElementById("legend");
          if (_legend?.parentNode) {
            _legend.parentNode.removeChild(_legend);
          }
        }
      }

      const legendControl = new LegendControl();
      this.bookingDetailsMapBoxMap.addControl(legendControl, "bottom-left");
    }
  }

  /**
   * Initializes and sets the Google map with markers and paths.
   */
  setGoogleMap(): void {
    const bounds = new google.maps.LatLngBounds();

    this.bookingDetailsGoogleMap = this.mapHelper.initializeGoogleMap("bookingDetailsMap", {
      zoom: 12,
      center: { lat: this.bookingDetails.pickupAddress?.latitude ?? 0, lng: this.bookingDetails.pickupAddress?.longitude ?? 0 }
    });

    this.setLegendOnMap();

    if (this.pickupMarker) {
      this.pickupMarker.setMap(null);
      this.pickupMarker = null;
    }

    const pickupPosition = new google.maps.LatLng({ lat: this.bookingDetails.pickupAddress?.latitude ?? 0, lng: this.bookingDetails.pickupAddress?.longitude ?? 0 });

    if (pickupPosition) {
      this.pickupMarker = new google.maps.Marker({
        position: pickupPosition,
        map: this.bookingDetailsGoogleMap,
        draggable: false,
        icon: {
          url: this.helper.MapPinImages.PickupMapPin, // url
          scaledSize: new google.maps.Size(20, 40) // scaled size
        }
      });

      bounds.extend(this.pickupMarker.position);
    }

    if (this.bookingDetails.destinationAddresses.length > 1) {
      const stopAddresses = JSON.parse(JSON.stringify(this.bookingDetails.destinationAddresses.slice(0, -1)));

      stopAddresses.forEach((address: any, index: number) => {
        let stopPosition = new google.maps.LatLng({ lat: address?.latitude, lng: address?.longitude });

        const marker = new google.maps.Marker({
          position: stopPosition,
          map: this.bookingDetailsGoogleMap,
          icon: {
            url: this.helper.MapPinImages.StopMapPin, // url
            scaledSize: new google.maps.Size(20, 40) // scaled size
          }
        });

        // Create a custom overlay for the label
        const label = new google.maps.OverlayView();
        label.onAdd = function (): void {
          const div = document.createElement("div");
          div.style.position = "absolute";
          div.style.color = "#fff";
          div.style.fontSize = "14px";
          div.style.fontWeight = "medium";
          div.style.height = "20px";
          div.style.width = "20px";
          div.style.display = "flex";
          div.style.justifyContent = "center";
          div.style.alignItems = "center";
          div.style.borderRadius = "5px";

          div.innerText = (index + 1).toString();

          this.div = div;

          // Add the label to the map's overlay layer
          const panes = this.getPanes();
          panes.overlayImage.appendChild(div);
        };

        label.draw = function (): void {
          const projection = this.getProjection();
          const position = projection.fromLatLngToDivPixel(stopPosition);

          const div = this.div;
          div.style.left = `${position.x - 10}px`;
          div.style.top = `${position.y - 39}px`; // Adjust the "-40" value to position the label above the marker
        };

        label.onRemove = function (): void {
          this.div.parentNode.removeChild(this.div);
          this.div = null;
        };

        // Bind the position of the label to the marker
        label.setPosition = function (latLng: any): void {
          stopPosition = latLng;
        };

        label.setMap(this.bookingDetailsGoogleMap);

        // Extend bounds to include this marker's position
        bounds.extend(marker.position);
      });
    }

    if (this.destinationMarker) {
      this.destinationMarker.setMap(null);
      this.destinationMarker = null;
    }

    const destinationAddresses = JSON.parse(JSON.stringify(this.bookingDetails.destinationAddresses.slice(-1)));

    if (destinationAddresses[0]?.latitude) {
      const destinationPosition = new google.maps.LatLng({ lat: destinationAddresses[0]?.latitude, lng: destinationAddresses[0]?.longitude });

      if (destinationPosition) {
        this.destinationMarker = new google.maps.Marker({
          position: destinationPosition,
          map: this.bookingDetailsGoogleMap,
          draggable: false,
          icon: {
            url: this.helper.MapPinImages.DestinationMapPin, // url
            scaledSize: new google.maps.Size(20, 40) // scaled size
          }
        });

        bounds.extend(this.destinationMarker.position);
      }
    }

    if (this.bookingDetails.confirmedDriver?.location?.coordinates?.length > 0) {
      const location = new google.maps.LatLng(this.initDriverLocationMap());
      this.driverMarker = new google.maps.Marker({
        position: location,
        map: this.bookingDetailsGoogleMap,
        draggable: false,
        icon: {
          url: this.helper.MapPinImages.DriverMapPin, // url
          scaledSize: new google.maps.Size(30, 30) // scaled size
        }
      });
      bounds.extend(this.driverMarker.position);
    }

    if (this.driverToPickupPath) {
      this.driverToPickupPath.setMap(null);
      this.driverToPickupPath = null;
    }
    if (this.tripPath) {
      this.tripPath.setMap(null);
      this.tripPath = null;
    }

    this.driverToPickupPath = new google.maps.Polyline({
      path: this.driverToPickupLocations,
      geodesic: true,
      strokeColor: "#00FF00",
      strokeOpacity: 1.0,
      strokeWeight: 3,
      zIndex: 2
    });
    this.driverToPickupPath.setMap(this.bookingDetailsGoogleMap);

    this.tripPath = new google.maps.Polyline({
      path: this.tripLocations,
      geodesic: true,
      strokeColor: "#FF0000",
      strokeOpacity: 1.0,
      strokeWeight: 3,
      zIndex: 2
    });
    this.tripPath.setMap(this.bookingDetailsGoogleMap);

    this.setEstimatedPathOnGoogleMap();

    this.bookingDetailsGoogleMap.fitBounds(bounds);
  }

  /**
   * Sets the estimated path on the Google map.
   */
  setEstimatedPathOnGoogleMap(): void {
    if (
      this.bookingDetails?.bookingInvoice?.estimated?.directionPath &&
      this.bookingHistoryMainType !== this.helper.BookingHistoryMainType.COMPLETED &&
      !this.estimatedBookingPath
    ) {
      const decodedPath = google.maps.geometry.encoding.decodePath(this.bookingDetails?.bookingInvoice?.estimated?.directionPath);

      // Create a polyline and set the map
      this.estimatedBookingPath = new google.maps.Polyline({
        path: decodedPath,
        geodesic: true,
        strokeColor: "#000",
        strokeOpacity: 1.0,
        strokeWeight: 3
      });

      // Set the polyline on the map
      this.estimatedBookingPath.setMap(this.bookingDetailsGoogleMap);
    }
  }

  /**
   * Initializes the driver's location on the map.
   *
   * @returns The driver's current location.
   */
  initDriverLocationMap(): { lat: number; lng: number } {
    let driverCurrentLocation: { lat: number; lng: number };
    const driverCreateTripLoaction = {
      lat: this.bookingDetails.confirmedDriver?.location?.coordinates[1] ?? 0,
      lng: this.bookingDetails.confirmedDriver?.location?.coordinates[0] ?? 0
    };

    if (this.bookingDetails.status < this.helper.BookingStatus.STARTED && this.driverToPickupLocations.length > 0) {
      const lastLocation = this.driverToPickupLocations[this.driverToPickupLocations.length - 1];
      driverCurrentLocation = { lat: lastLocation.lat ?? driverCreateTripLoaction.lat, lng: lastLocation.lng ?? driverCreateTripLoaction.lng };
    } else if (this.tripLocations.length > 0) {
      const lastLocation = this.tripLocations[this.tripLocations.length - 1];
      driverCurrentLocation = { lat: lastLocation.lat ?? driverCreateTripLoaction.lat, lng: lastLocation.lng ?? driverCreateTripLoaction.lng };
    } else {
      driverCurrentLocation = driverCreateTripLoaction;
    }

    return driverCurrentLocation;
  }

  /**
   * Initializes the Mapbox map for booking details.
   */
  async initBookingMapBoxMap(): Promise<void> {
    const mapElement: HTMLElement | null = document.getElementById("bookingDetailsMap");
    if (!mapElement) return;
    const session = this.session;
    this.mb = await this.mapHelper.loadMapbox();
    if (session !== this.session || this.destroyed || !mapElement.isConnected) return;
    this.bookingDetailsMapBoxMap?.remove();

    // Initialize the Mapbox map
    this.bookingDetailsMapBoxMap = new this.mb.Map({
      container: mapElement,
      style: "mapbox://styles/mapbox/streets-v11",
      center: [this.bookingDetails.pickupAddress?.longitude ?? 0, this.bookingDetails.pickupAddress?.latitude ?? 0],
      zoom: 12,
      accessToken: this.helper.MapBoxAccessToken
    });
  }

  /**
   * Sets the Mapbox map with markers and paths.
   */
  async setMapBoxMap(): Promise<void> {
    const session = this.session;
    this.mb = this.mb || (await this.mapHelper.loadMapbox());
    if (session !== this.session || this.destroyed) return;
    // Prevent Mapbox from sending telemetry events
    const originalSendBeacon = navigator.sendBeacon;
    navigator.sendBeacon = function (url: string, data?: BodyInit | null): boolean {
      if (url.includes("events.mapbox.com")) {
        return true; // block the request
      }
      return originalSendBeacon.call(this, url, data);
    };

    await this.initBookingMapBoxMap();
    if (!this.bookingDetailsMapBoxMap || session !== this.session || this.destroyed) return;

    const bounds = new this.mb.LngLatBounds();

    this.bookingDetailsMapBoxMap.on("load", () => {
      this.setLegendOnMap();

      this.setMapboxPickupMarker(bounds);

      this.setMapboxDestinationMarker(bounds);

      this.setMapboxStopMarkers(bounds);

      this.setMapboxDriverMarker(bounds);

      this.removeTripPath();

      const driverToPickupPathCoordinates = this.driverToPickupLocations.map((location) => [location.lng, location.lat]); // Filter out invalid coordinates

      if (driverToPickupPathCoordinates.length > 0) {
        this.bookingDetailsMapBoxMap.addSource("driverToPickupPath", {
          type: "geojson",
          data: {
            type: "Feature",
            properties: {},
            geometry: {
              type: "LineString",
              coordinates: driverToPickupPathCoordinates
            }
          }
        });

        this.bookingDetailsMapBoxMap.addLayer({
          id: "driverToPickupPath",
          type: "line",
          source: "driverToPickupPath",
          layout: {
            "line-join": "round",
            "line-cap": "round"
          },
          paint: {
            "line-color": "#00FF00",
            "line-opacity": 1.0,
            "line-width": 2
          }
        });

        this.driverToPickupPath = this.bookingDetailsMapBoxMap.getSource("driverToPickupPath");

        driverToPickupPathCoordinates?.forEach((coord: any) => bounds.extend(coord));
      }

      const tripCoordinates = this.tripLocations.map((location) => [location.lng, location.lat]); // Filter out invalid coordinates

      if (tripCoordinates.length > 0) {
        this.bookingDetailsMapBoxMap.addSource("tripPath", {
          type: "geojson",
          data: {
            type: "Feature",
            properties: {},
            geometry: {
              type: "LineString",
              coordinates: tripCoordinates
            }
          }
        });

        this.bookingDetailsMapBoxMap.addLayer({
          id: "tripPath",
          type: "line",
          source: "tripPath",
          layout: {
            "line-join": "round",
            "line-cap": "round"
          },
          paint: {
            "line-color": "#FF0000",
            "line-opacity": 1.0,
            "line-width": 2
          }
        });

        this.tripPath = this.bookingDetailsMapBoxMap.getSource("tripPath");

        tripCoordinates?.forEach((coord: any) => bounds.extend(coord));
      }

      this.bookingDetailsMapBoxMap.fitBounds(bounds, { padding: 20 });
    });
  }

  /**
   * Sets the pickup marker on the Mapbox map.
   *
   * @param bounds The map bounds to extend.
   */
  setMapboxPickupMarker(bounds: any): void {
    if (this.pickupMarker) {
      this.pickupMarker.remove();
      this.pickupMarker = null;
    }

    const pickupPosition: [number, number] = [this.bookingDetails.pickupAddress?.longitude ?? 0, this.bookingDetails.pickupAddress?.latitude ?? 0];

    if (pickupPosition) {
      this.pickupMarker = new this.mb.Marker({
        draggable: false,
        element: this.createCustomMarkerElement(this.helper.MapPinImages.PickupMapPin)
      })
        .setLngLat(pickupPosition)
        .addTo(this.bookingDetailsMapBoxMap);

      bounds.extend(pickupPosition);
    }
  }

  /**
   * Sets the destination marker on the Mapbox map.
   *
   * @param bounds The map bounds to extend.
   */
  setMapboxDestinationMarker(bounds: any): void {
    if (this.destinationMarker) {
      this.destinationMarker.remove();
      this.destinationMarker = null;
    }

    const destinationAddresses = JSON.parse(JSON.stringify(this.bookingDetails.destinationAddresses.slice(-1)));

    if (destinationAddresses[0]) {
      const destinationPosition: [number, number] = [destinationAddresses[0]?.longitude, destinationAddresses[0]?.latitude];

      if (destinationPosition) {
        this.destinationMarker = new this.mb.Marker({
          draggable: false,
          element: this.createCustomMarkerElement(this.helper.MapPinImages.DestinationMapPin)
        })
          .setLngLat(destinationPosition)
          .addTo(this.bookingDetailsMapBoxMap);

        bounds.extend(destinationPosition);
      }
    }
  }

  /**
   * Sets the stop markers on the Mapbox map.
   *
   * @param bounds The map bounds to extend.
   */
  setMapboxStopMarkers(bounds: any): void {
    if (this.bookingDetails.destinationAddresses.length > 1) {
      const stopAddresses = JSON.parse(JSON.stringify(this.bookingDetails.destinationAddresses.slice(0, -1)));

      stopAddresses.forEach((address: any) => {
        const stopPosition: [number, number] = [address?.longitude, address?.latitude];

        if (stopPosition) {
          new this.mb.Marker({
            draggable: false,
            element: this.createCustomMarkerElement(this.helper.MapPinImages.DestinationMapPin)
          })
            .setLngLat(stopPosition)
            .addTo(this.bookingDetailsMapBoxMap);

          bounds.extend(stopPosition);
        }
      });
    }
  }

  /**
   * Sets the driver marker on the Mapbox map.
   *
   * @param bounds The map bounds to extend.
   */
  setMapboxDriverMarker(bounds: any): void {
    if (this.bookingDetails.confirmedDriver?.location?.coordinates?.length > 0) {
      const driverPosition: [number, number] = [this.initDriverLocationMap().lng, this.initDriverLocationMap().lat];

      this.driverMarker = new this.mb.Marker({
        draggable: false,
        element: this.createCustomMarkerElement(this.helper.MapPinImages.DriverMapPin)
      })
        .setLngLat(driverPosition)
        .addTo(this.bookingDetailsMapBoxMap);

      bounds.extend(driverPosition);
    }
  }

  /**
   * Removes the trip path from the Mapbox map.
   */
  removeTripPath(): void {
    if (this.tripPath && this.bookingDetailsMapBoxMap?.getLayer("tripPath")) {
      this.bookingDetailsMapBoxMap.removeLayer("tripPath");
    }
    if (this.tripPath && this.bookingDetailsMapBoxMap?.getSource("tripPath")) {
      this.bookingDetailsMapBoxMap.removeSource("tripPath");
    }
  }

  /**
   * Removes the driver to pickup trip path from the Mapbox map.
   */
  removeDriverToPickupTripPath(): void {
    if (this.driverToPickupPath && this.bookingDetailsMapBoxMap?.getLayer("driverToPickupPath")) {
      this.bookingDetailsMapBoxMap.removeLayer("driverToPickupPath");
    }
    if (this.driverToPickupPath && this.bookingDetailsMapBoxMap?.getSource("driverToPickupPath")) {
      this.bookingDetailsMapBoxMap.removeSource("driverToPickupPath");
    }
  }

  /**
   * Creates a custom marker element for the map.
   *
   * @param iconUrl The URL of the marker icon.
   * @returns The custom marker element.
   */
  createCustomMarkerElement(iconUrl: string): HTMLElement {
    const markerElement = document.createElement("div");
    const markerImage = document.createElement("img");
    markerImage.src = iconUrl;
    markerImage.style.width = "30px";
    markerImage.style.height = "30px";
    markerElement.appendChild(markerImage);
    return markerElement;
  }

  /**
   * Handles tab click events and updates the component state.
   *
   * @param tab The clicked tab.
   */
  async onTabClick(tab: string): Promise<void> {
    const session = this.session;
    this.tabType = tab;
    if (tab === "basicDetails") {
      if (this.bookingHistoryMainType === this.helper.BookingHistoryMainType.RUNNING) {
        this.tripLocations = [];
        this.driverToPickupLocations = [];
        await this.getBookingLocation();
      }

      if (this.estimatedBookingPath) {
        this.estimatedBookingPath.setMap(null);
        this.estimatedBookingPath = null;
      }

      setTimeout(() => {
        if (session !== this.session || this.destroyed) return;
        if (this.helper.mapTypeMapBox()) {
          this.setMapBoxMap();
        }
        if (this.helper.mapTypeGoogle()) {
          this.setGoogleMap();
        }
      }, 500);
    }
    if (tab === "chat") {
      if (this.bookingDetails?.confirmedDriver?.id) {
        this.chatActiveTab = 1;
        this.onChatTabClick(this.helper.ChatType.CUSTOMER_DRIVER_CHAT);
      } else if (this.bookingDetails?.quickCommerce?.merchantDetail?.id) {
        this.chatActiveTab = 2;
        this.onChatTabClick(this.helper.ChatType.CUSTOMER_MERCHANT_CHAT);
      }
    }

    setTimeout(() => {
      this.scrollToTop();
    }, 200);
  }

  async onChatTabClick(tab: string): Promise<void> {
    if (this.chatTabType === tab) return;

    this.chatTabType = tab;

    if (this.chatService.isSocketConnected) {
      this.chatService.disConnectChatSockets();
    }

    const parameters: any = {
      event: this.helper.SocketEvents.JOIN_CHAT,
      data: {
        referenceId: this.bookingDetails._id,
        chatType: this.chatTabType
      }
    };
    await this.chatService.connectChatSocket(this.helper.SocketEvents.JOIN_CHAT, parameters);

    setTimeout(() => {
      this.scrollToTop();
    }, 200);
  }

  /**
   * Shows the map modal.
   */
  showMap(): void {
    this.mapModalRef = this.modalService.open(this.mapModal, { size: "lg", centered: true, backdrop: "static" });
    this.mapModalRef.result.then(
      () => {},
      () => {}
    );

    setTimeout(() => {
      if (this.helper.mapTypeMapBox()) {
        this.setMapBoxMap();
      }
      if (this.helper.mapTypeGoogle()) {
        this.setGoogleMap();
      }
    }, 500);
  }

  /**
   * Closes the map modal.
   */
  closeMapModal(): void {
    this.mapModalRef.dismiss("Cross click");
  }

  /**
   * Scrolls the modal content to the top.
   */
  scrollToTop(): void {
    const modalElement = document.querySelector(".modal-body") as HTMLElement;

    if (modalElement) {
      modalElement.scrollTop = 0;
    }
  }

  /**
   * Checks if the booking is a delivery booking.
   * @returns True if the booking is a delivery booking, false otherwise.
   */
  isDeliveryBooking(): boolean {
    return this.bookingDetails.bookingType === this.helper.PriceMode.DELIVERY || this.bookingDetails.bookingType === this.helper.PriceMode.PICKUP;
  }

  isServiceBooking(): boolean {
    return this.bookingDetails.businessType === this.helper.BusinessType.SERVICE;
  }

  isDeliveryPickUpBooking(): boolean {
    return this.bookingDetails.bookingType === this.helper.PriceMode.PICKUP;
  }

  isNotDeliveryBooking(): boolean {
    return this.bookingDetails.bookingType !== this.helper.PriceMode.DELIVERY && this.bookingDetails.bookingType !== this.helper.PriceMode.PICKUP;
  }

  isNotServiceBooking(): boolean {
    return this.bookingDetails.businessType !== this.helper.BusinessType.SERVICE;
  }


  handleImageError(event: Event): void {
    (event.target as HTMLImageElement).src = this.helper.DefaultImages.DefaultUserImage;
  }
  
  openImagePreview(imageUrl: string): void {
    window.open(imageUrl, '_blank');
  }

  /**
   * Read-only payment summary from the booking invoice already loaded
   * by GET /api/booking/:id or GET /api/booking_history/:id.
   * Stripe intent id and card last-4 render only when that payload includes them.
   */
  tripPayment(): any {
    const booking = this.bookingDetails;
    const invoice = booking?.bookingInvoice;
    if (!invoice) return null;

    const arrived = Number(booking.status) >= this.helper.BookingStatus.ARRIVED_AT_DESTINATION;
    const actual = invoice.actual;
    const estimated = invoice.estimated;
    const usable = (fare: any): boolean => !!fare && (Number(fare.total) > 0 || (fare.charges?.length ?? 0) > 0 || (fare.additionalPrices?.length ?? 0) > 0);
    let fare = estimated || actual || {};
    let source = "Estimated fare";
    if ((arrived && usable(actual)) || (!usable(estimated) && usable(actual))) {
      fare = actual;
      source = "Actual fare";
    }

    const rows = [...(fare.charges || []), ...(fare.additionalPrices || []), ...(fare.accessibilityPrices || []), ...(fare.taxPrices || [])];
    const hasBreakdown = rows.length > 0;
    const buckets = { base: 0, distance: 0, time: 0, discount: 0, tip: 0, toll: 0, other: 0 };
    rows.forEach((row) => {
      buckets[this.paymentBucket(row) as keyof typeof buckets] += this.paymentChargeAmount(row);
    });

    const promo = invoice.promoDetail || {};
    const promoAmount = Math.abs(Number(promo.discount) || Number(promo.promoBonus) || 0);
    const discountTitles = ["promoBonus", "offerBonus", "corporateDiscount", "spinBonus"];
    const hasDiscountCharge = rows.some((row) => discountTitles.includes(String(row?.title)));
    if (!hasDiscountCharge && promoAmount) buckets.discount -= promoAmount;
    const corporate = Math.abs(Number(fare.corporateDiscount) || 0);
    if (!rows.some((row) => row?.title === "corporateDiscount") && corporate) buckets.discount -= corporate;
    const spin = Math.abs(Number(invoice.spinReward?.discountApplied) || 0);
    if (!rows.some((row) => row?.title === "spinBonus") && spin) buckets.discount -= spin;
    if (!hasDiscountCharge && !promoAmount) {
      (fare.appliedOffer || []).forEach((offer: any) => {
        const amount = Math.abs(Number(offer?.discount ?? offer?.price ?? offer?.amount) || 0);
        if (amount) buckets.discount -= amount;
      });
    }

    const invoiceTip = Number(invoice.tip) || 0;
    if (invoiceTip > 0) buckets.tip = invoiceTip;

    const moneyOrDash = (amount: number, always: boolean): string => (always || amount !== 0 ? this.paymentMoney(amount) : "—");
    const promoCode = promo.code ? ` (${promo.code})` : "";
    const lines = [
      { id: "trip-pay-base", label: "Base", text: moneyOrDash(buckets.base, hasBreakdown), discount: false, total: false },
      { id: "trip-pay-distance", label: "Distance", text: moneyOrDash(buckets.distance, hasBreakdown), discount: false, total: false },
      { id: "trip-pay-time", label: "Time", text: moneyOrDash(buckets.time, hasBreakdown), discount: false, total: false },
      { id: "trip-pay-discount", label: `Discounts and promo${promoCode}`, text: moneyOrDash(buckets.discount, hasBreakdown), discount: buckets.discount < 0, total: false },
      { id: "trip-pay-tip", label: "Tips", text: moneyOrDash(buckets.tip, hasBreakdown || invoiceTip > 0), discount: false, total: false },
      { id: "trip-pay-toll", label: "Tolls", text: moneyOrDash(buckets.toll, hasBreakdown), discount: false, total: false }
    ];
    if (Math.abs(buckets.other) > 0.004) {
      lines.push({ id: "trip-pay-other", label: "Other", text: this.paymentMoney(buckets.other), discount: buckets.other < 0, total: false });
    }
    const total = Number(fare.total);
    lines.push({
      id: "trip-pay-total",
      label: "Total",
      text: Number.isFinite(total) ? this.paymentMoney(total) : "—",
      discount: false,
      total: true
    });

    const txns = this.paymentTransactions(booking, invoice);
    const intents = [...new Set(txns.map((txn) => this.paymentIntentId(txn)).filter((id): id is string => !!id))];
    ["paymentIntentId", "paymentIntent", "stripePaymentIntentId", "transactionId"].forEach((key) => {
      const id = this.paymentIntentId({ transactionId: invoice[key] }) || this.paymentIntentId({ transactionId: booking[key] });
      if (id && !intents.includes(id)) intents.push(id);
    });
    const last4 =
      this.paymentLast4(invoice) ||
      this.paymentLast4(booking) ||
      txns.map((txn) => this.paymentLast4(txn)).find((value) => !!value) ||
      null;

    const refunds = this.paymentRefunds(invoice, txns);
    const refundTotal = refunds.reduce((sum, row) => sum + row.raw, 0);
    const status = this.paymentStatus(invoice, refundTotal, Number(fare.total) || 0);
    const payout = this.paymentPayout(invoice, fare, hasBreakdown, Number(fare.total) || 0);

    return {
      source,
      method: this.paymentMethod(invoice),
      split: this.paymentSplit(invoice),
      status: status.label,
      statusTone: status.tone,
      lines,
      intent: intents.length ? intents.join(", ") : "Not returned",
      last4: last4 || "Not returned",
      payout: payout.text,
      payoutNotes: payout.notes,
      isCard: this.isCardPayment(invoice),
      refunds: refunds.map((row) => ({ label: row.label, detail: row.detail, amount: row.amount }))
    };
  }

  /**
   * True when the fare was actually taken on a card rail. Cash, wallet, account and voucher trips
   * have no intent and no last 4, so the Card block in the payment tab is noise on them — it used
   * to render with two empty values on every cash trip.
   */
  private isCardPayment(invoice: any): boolean {
    return this.cardPaymentModes().has(Number(invoice?.paymentMode));
  }

  private cardPaymentModes(): Set<number> {
    return new Set([
      this.helper.PaymentMode.Stripe,
      this.helper.PaymentMode.Paystack,
      this.helper.PaymentMode.Razorpay,
      this.helper.PaymentMode.Mercado,
      this.helper.PaymentMode.Payu,
      this.helper.PaymentMode.Pago,
      this.helper.PaymentMode.PagoC2P,
      this.helper.PaymentMode.Zaincash,
      this.helper.PaymentMode.Hyperpay,
      this.helper.PaymentMode.Nestpay,
      this.helper.PaymentMode.TapToPay
    ]);
  }

  private paymentMethod(invoice: any): string {
    const mode = Number(invoice?.paymentMode);
    const cardModes = this.cardPaymentModes();
    let method = "Cash";
    if (mode === this.helper.PaymentMode.Wallet) method = "Wallet";
    else if (mode === this.helper.PaymentMode.Paylater) method = "Account";
    else if (mode === this.helper.PaymentMode.Voucher) method = "Voucher";
    else if (cardModes.has(mode)) method = "Card";
    if (invoice?.isApplePay) method += " · Apple Pay";
    if (invoice?.isGooglePay) method += " · Google Pay";
    return method;
  }

  private paymentSplit(invoice: any): string | null {
    const detail = invoice?.paymentDetail || {};
    const method = this.paymentMethod(invoice).split(" · ")[0];
    const parts: string[] = [];
    if (Number(detail.cash) > 0) parts.push(`Cash ${this.paymentMoney(detail.cash)}`);
    if (Number(detail.wallet) > 0) parts.push(`Wallet ${this.paymentMoney(detail.wallet)}`);
    if (Number(detail.other) > 0) parts.push(`${method === "Card" ? "Card" : "Other"} ${this.paymentMoney(detail.other)}`);
    if (Number(detail.remaining) > 0) parts.push(`Remaining ${this.paymentMoney(detail.remaining)}`);
    if (parts.length > 1) return parts.join(" · ");
    if (parts.length === 1 && !parts[0].startsWith(method)) return parts[0];
    return null;
  }

  private paymentStatus(invoice: any, refundTotal: number, fareTotal: number): { label: string; tone: string } {
    const status = Number(invoice?.paymentStatus);
    const held =
      !!invoice?.isCapturePaymentPending ||
      status === this.helper.PaymentTransactionStatus.CAPTURE_REQUIRED ||
      status === this.helper.PaymentTransactionStatus.INITIATED;
    let label = "Pending";
    let tone = "other";
    if (held) {
      label = "Authorized / held";
      tone = "held";
    }
    if (status === this.helper.PaymentTransactionStatus.PAID) {
      label = "Captured";
      tone = "captured";
    }
    if (status === this.helper.PaymentTransactionStatus.FAILED) {
      label = "Failed";
      tone = "failed";
    }
    if (status === this.helper.PaymentTransactionStatus.CANCELLED) {
      label = "Released";
      tone = "other";
    }
    if (status === this.helper.PaymentTransactionStatus.PAY_LATER) {
      label = "Pay later";
      tone = "other";
    }
    if (status !== this.helper.PaymentTransactionStatus.FAILED && refundTotal > 0 && (fareTotal <= 0 || refundTotal + 0.009 >= fareTotal)) {
      label = "Refunded";
      tone = "refunded";
    }
    return { label, tone };
  }

  private paymentTransactions(booking: any, invoice: any): any[] {
    return [booking?.paymentTransactions, invoice?.paymentTransactions, booking?.paymentTransaction, invoice?.paymentTransaction]
      .flatMap((value) => (Array.isArray(value) ? value : value ? [value] : []))
      .filter((txn) => txn && typeof txn === "object");
  }

  private paymentIntentId(txn: any): string | null {
    const candidates = [txn?.transactionId, txn?.paymentIntent, txn?.paymentIntentId, txn?.transactionReference?.paymentIntent, txn?.transactionReference?.id];
    for (const value of candidates) {
      if (typeof value !== "string") continue;
      const id = value.trim();
      if (!id || /^[a-f0-9]{24}$/i.test(id)) continue;
      const compact = id.replace(/\s/g, "");
      const digits = compact.replace(/\D/g, "");
      if (digits.length >= 12 && digits.length === compact.length) continue;
      return id;
    }
    return null;
  }

  private paymentLast4(source: any): string | null {
    if (!source || typeof source !== "object") return null;
    const candidates = [
      source.lastFour,
      source.last4,
      source.cardLastFour,
      source.card?.lastFour,
      source.card?.last4,
      source.transactionReference?.lastFour,
      source.transactionReference?.last4,
      source.transactionReference?.card?.lastFour,
      source.transactionReference?.card?.last4
    ];
    for (const value of candidates) {
      if (value == null || value === "") continue;
      const digits = String(value).replace(/\D/g, "");
      if (digits.length < 4) continue;
      return `•••• ${digits.slice(-4)}`;
    }
    return null;
  }

  private paymentRefunds(invoice: any, txns: any[]): Array<{ label: string; detail: string; amount: string; raw: number }> {
    const itemized = txns
      .map((txn) => {
        const raw = Number(txn?.refundAmount) || 0;
        if (raw <= 0) return null;
        const when = txn.createdAt || txn.updatedAt;
        let clock = "";
        if (when) {
          const zone = this.bookingTimezone || this.helper.displayTimezone;
          clock = this.helper.moment(when).tz(zone).format("D MMM YYYY, h:mm:ss A");
        }
        const detail = [this.paymentIntentId(txn), txn.refundReason, clock].filter(Boolean).join(" · ");
        return { label: "Refund", detail, amount: this.paymentMoney(raw), raw };
      })
      .filter((row): row is { label: string; detail: string; amount: string; raw: number } => !!row);
    if (itemized.length) return itemized;

    const wallet = Number(invoice?.refundDetail?.wallet) || 0;
    const other = Number(invoice?.refundDetail?.other) || 0;
    const rows: Array<{ label: string; detail: string; amount: string; raw: number }> = [];
    if (wallet > 0) rows.push({ label: "Wallet", detail: "Invoice refund total", amount: this.paymentMoney(wallet), raw: wallet });
    if (other > 0) {
      const method = this.paymentMethod(invoice).split(" · ")[0];
      rows.push({ label: method === "Card" ? "Card" : "Other", detail: "Invoice refund total", amount: this.paymentMoney(other), raw: other });
    }
    return rows;
  }

  private paymentPayout(invoice: any, fare: any, hasBreakdown: boolean, fareTotal: number): { text: string; notes: string[] } {
    const profit = Number(fare?.driverProfit);
    const notes: string[] = [];
    const cash = Number(invoice?.driverCashToCollect) || 0;
    const adjustment = Number(invoice?.driverWalletAdjustment) || 0;
    const incentive = Number(invoice?.pickupIncentive?.amount) || 0;
    const settledWallet = Number(invoice?.driverSettlementDetail?.wallet) || 0;
    const settledOther = Number(invoice?.driverSettlementDetail?.other) || 0;
    if (cash > 0) notes.push(`Collect in cash ${this.paymentMoney(cash)}`);
    if (adjustment) notes.push(`Wallet adjustment ${this.paymentMoney(adjustment)}`);
    if (incentive > 0) notes.push(`Pickup incentive ${this.paymentMoney(incentive)} (not in the rider fare)`);
    if (settledWallet > 0) notes.push(`Settled to wallet ${this.paymentMoney(settledWallet)}`);
    if (settledOther > 0) notes.push(`Settled outside the wallet ${this.paymentMoney(settledOther)}`);
    const known = Number.isFinite(profit) && (profit !== 0 || hasBreakdown || notes.length > 0);
    const driverStatus: Record<number, string> = { 1: "Payout pending", 2: "Transferred to wallet", 3: "Transferred to bank" };
    const statusLabel = driverStatus[Number(invoice?.driverPaymentStatus)];
    if (known && statusLabel) notes.push(statusLabel);
    if (!known) return { text: "Not returned", notes: [] };
    let text = this.paymentMoney(profit);
    if (fareTotal > 0 && Number.isFinite(profit)) text += ` · ${Math.round((profit / fareTotal) * 100)}% of fare`;
    return { text, notes };
  }

  private paymentBucket(row: any): string {
    const title = String(row?.title || "");
    const type = Number(row?.type);
    if (row?.isTip || title === "tipPrice") return "tip";
    if (["promoBonus", "offerBonus", "corporateDiscount", "spinBonus"].includes(title)) return "discount";
    if (type === 7 || /toll/i.test(title)) return "toll";
    if (["distancePrice", "outsideBoundaryDistancePrice"].includes(title) || type === 8) return "distance";
    if (
      [
        "timePrice",
        "waitingTimePrice",
        "airportWaitingTimePrice",
        "stopWaitingTimePrice",
        "trafficTimePrice",
        "outsideBoundaryTimePrice",
        "outsideBoundaryWaitingTimePrice",
        "outsideBoundaryAirportWaitingTimePrice",
        "outsideBoundaryStopWaitingTimePrice",
        "outsideBoundaryTrafficPrice"
      ].includes(title) ||
      type === 9
    ) {
      return "time";
    }
    if (
      [
        "bookingFee",
        "minimumFee",
        "weightPrice",
        "pickupCharge",
        "dropoffCharge",
        "airportPrice",
        "airportPickupPrice",
        "zoneToZonePrice",
        "cityToCityPrice",
        "bidPrice",
        "cartPrice",
        "deliveryPrice",
        "cancellationPrice"
      ].includes(title)
    ) {
      return "base";
    }
    return "other";
  }

  private paymentChargeAmount(row: any): number {
    const price = Number(row?.price);
    if (Number.isFinite(price) && price !== 0) return price;
    const discounted = Number(row?.discountedPrice);
    if (Number.isFinite(discounted)) return discounted;
    return Number.isFinite(price) ? price : 0;
  }

  private paymentMoney(amount: number): string {
    const invoice = this.bookingDetails?.bookingInvoice;
    const sign = invoice?.currencySign || this.bookingDetails?.currencySign || "";
    const digits = Number.isFinite(Number(this.decimalPointValue)) ? Number(this.decimalPointValue) : Number(invoice?.decimalPointValue ?? 2);
    const text = Math.abs(Number(amount) || 0).toFixed(Number.isFinite(digits) ? digits : 2);
    const body = this.setCurrencySign === this.helper.SetCurrencySignUnit.RIGHT ? `${text}${sign ? " " + sign : ""}` : `${sign ? sign + " " : ""}${text}`;
    return amount < 0 ? `-${body}` : body;
  }
}
